import React, { useState, useRef, useEffect } from 'react';
import { signatureAPI } from '../api';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Pen, Upload, Trash2, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SignatureManager = () => {
  const [signature, setSignature] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSignature();
  }, []);

  const fetchSignature = async () => {
    try {
      const res = await signatureAPI.getMy();
      setSignature(res.data?.has_signature ? res.data : null);
    } catch (err) {
      console.error('Failed to fetch signature:', err);
    } finally {
      setLoading(false);
    }
  };

  // Canvas drawing
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1A1D21';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const startDraw = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDraw = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const handleSaveDrawn = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setSaving(true);
    try {
      const dataUrl = canvas.toDataURL('image/png');
      const res = await signatureAPI.saveDrawn({ signature_data: dataUrl });
      setSignature(res.data);
      toast.success('Signature saved');
    } catch (err) {
      toast.error('Failed to save signature');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSaving(true);
    try {
      const res = await signatureAPI.uploadImage(file);
      setSignature(res.data);
      toast.success('Signature uploaded');
    } catch (err) {
      toast.error('Failed to upload signature');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await signatureAPI.deleteMy();
      setSignature(null);
      toast.success('Signature removed');
    } catch (err) {
      toast.error('Failed to delete signature');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-[#00A3A3]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="signature-manager">
      <Card className="border-[#E2E6EA]">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Pen className="h-5 w-5 text-[#00A3A3]" />
            E-Signature
          </CardTitle>
          <CardDescription>
            Manage your electronic signature for resolutions and certificates
          </CardDescription>
        </CardHeader>
        <CardContent>
          {signature ? (
            <div className="space-y-4">
              <div className="border border-[#E2E6EA] rounded-lg p-4 bg-white">
                <p className="text-xs text-[#A8B1B9] mb-2">Your current signature:</p>
                <div className="flex items-center justify-center bg-gray-50 rounded p-4">
                  <img
                    src={`${BACKEND_URL}${signature.image_url}`}
                    alt="Your signature"
                    className="max-h-24 object-contain"
                    data-testid="current-signature-img"
                  />
                </div>
                <p className="text-xs text-[#A8B1B9] mt-2">
                  Type: {signature.type === 'drawn' ? 'Hand-drawn' : 'Uploaded image'}
                  {signature.updated_at && ` | Updated: ${new Date(signature.updated_at).toLocaleDateString()}`}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDelete}
                  disabled={saving}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="delete-signature-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <Tabs defaultValue="draw" className="space-y-4">
              <TabsList className="bg-[#F0F2F4]">
                <TabsTrigger value="draw" className="data-[state=active]:bg-white" data-testid="draw-tab">
                  <Pen className="h-4 w-4 mr-2" />
                  Draw
                </TabsTrigger>
                <TabsTrigger value="upload" className="data-[state=active]:bg-white" data-testid="upload-tab">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Image
                </TabsTrigger>
              </TabsList>

              <TabsContent value="draw" className="space-y-3">
                <div className="border border-[#E2E6EA] rounded-lg overflow-hidden bg-white">
                  <canvas
                    ref={canvasRef}
                    width={400}
                    height={150}
                    className="w-full cursor-crosshair touch-none"
                    onMouseDown={startDraw}
                    onMouseMove={draw}
                    onMouseUp={stopDraw}
                    onMouseLeave={stopDraw}
                    onTouchStart={startDraw}
                    onTouchMove={draw}
                    onTouchEnd={stopDraw}
                    data-testid="signature-canvas"
                  />
                </div>
                <p className="text-xs text-[#A8B1B9]">Draw your signature above using mouse or touch</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={clearCanvas} data-testid="clear-canvas-btn">
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveDrawn}
                    disabled={saving}
                    className="bg-[#00A3A3] hover:bg-[#008585]"
                    data-testid="save-drawn-btn"
                  >
                    {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                    Save Signature
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="upload" className="space-y-3">
                <div
                  className="border-2 border-dashed border-[#E2E6EA] rounded-lg p-8 text-center cursor-pointer hover:border-[#00A3A3] transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-signature-area"
                >
                  <Upload className="h-8 w-8 mx-auto mb-2 text-[#A8B1B9]" />
                  <p className="text-sm text-[#5A6570]">Click to upload signature image</p>
                  <p className="text-xs text-[#A8B1B9] mt-1">PNG or JPG, max 2MB</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleUpload}
                  className="hidden"
                  data-testid="signature-file-input"
                />
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SignatureManager;
